<template>
  <div class="cpd-content-container">
    <v-breadcrumbs :items="breadcrumbs" v-if="!isMobile">
      <template v-slot:divider>
        <v-icon>mdi-chevron-right</v-icon>
      </template>
    </v-breadcrumbs>
    <div class="cpd-header">
      <v-row>
        <v-col
          cols="12"
          sm="4"
          md="4"
          lg="4"
          xl="4"
          class="pa-md-0 pa-sm-0 py-0"
        >
          <div class="back">
            <button @click="$router.go(-1)">
              <v-icon>mdi-chevron-left</v-icon>
              Table of Contents
            </button>
          </div>
        </v-col>
        <v-col
          cols="12"
          sm="8"
          md="8"
          lg="8"
          xl="8"
          class="pa-md-0 pa-sm-0 py-0"
        >
          <div class="title-header">
            {{ chapterTitle }}
          </div>
        </v-col>
      </v-row>
    </div>
    <div class="cpd-content-body">
      <v-row>
        <div class="cpd-section-toc" v-if="!isMobile">
          <StickyTOC :subsections="this.sectionContent.Subsection" />
        </div>
        <div class="cpd-section">
          <div class="col-12 cpd-section-header">
            <div class="cpd-title">
              {{ chapterTitle }}
            </div>
            <div class="cpd-skills-dropdown">
              <v-select
                v-model="dropdownSelected"
                :items="skillMap"
                item-text="name"
                item-value="value"
                @change="switchToSection()"
                outlined
              ></v-select>
            </div>
          </div>
          <div class="col-12 cpd-section-content">
            <Subsection :subsections="this.sectionContent.Subsection" />
          </div>
          <BottomCard :skills="this.skillMap" />
        </div>
      </v-row>
    </div>
  </div>
</template>

<script lang="js">
import StickyTOC from '@/components/cpd-content/StickyTOC'
import Subsection from '@/components/cpd-content/Subsection'
import BottomCard from '@/components/cpd-content/BottomCard'
import { mapState, mapMutations } from 'vuex'
export default {
  name: 'CPD',
  components: {
    StickyTOC,
    Subsection,
    BottomCard
  },
  beforeMount () {
    this.onResize()
    window.addEventListener('resize', this.onResize, { passive: true })
    this.breadcrumbs = [{
      text: 'Resources',
      disabled: false,
      href: 'https://www.cambridge.org/go/resources',
      divider: '>'
    },
    {
      text: this.bookTitle,
      disabled: false,
      href: './',
      divider: '>'
    },
    {
      text: this.chapterTitle,
      disabled: true,
      href: '#',
      divider: '>'
    }]
  },
  beforeDestroy () {
    if (typeof window === 'undefined') return
    window.removeEventListener('resize', this.onResize, { passive: true })
  },
  mounted () {
    this.initializeSkillMap()
    this.formatContents()
  },
  updated () {
    this.formatContents()
    this.addGTMEvent()
  },
  data () {
    return {
      isMobile: false,
      breadcrumbs: null,
      skillMap: []
    }
  },
  methods: {
    ...mapMutations({
      setChapterTitle: 'book/SET_CHAPTER_TITLE',
      setSectionContent: 'book/SET_SECTION_CONTENT',
      setSkillSelected: 'book/SET_SKILL_SELECTED'
    }),
    async formatContents () {
      await MathJax.typesetPromise()
      cpdSetImagesLightBox()
      setTablesSideScroll()
    },
    initializeSkillMap () {
      for (const [key, value] of this.chapterMap.entries()) {
        let chapterName = value.ChapterName[0]
        let sections = value.Section
        let skillItem = {}
        for (const section of sections) {
          let type = section.$.type
          if (type === 'cpd-content') {
            skillItem['name'] = chapterName
            let itemValue = {}
            itemValue['chapterName'] = chapterName
            itemValue['sectionTitle'] = section.SectionName[0]
            itemValue['sectionData'] = section.SectionContent[0]
            if (section.SectionThumbnail) {
              itemValue['sectionThumbnail'] = section.SectionThumbnail[0]
            } else {
              itemValue['sectionThumbnail'] = '/img/default.png'
            }
            skillItem['value'] = itemValue
            break
          }
        }

        if (this.chapterTitle === skillItem['name']) {
          this.dropdownSelected = skillItem
          this.setSkillSelected(skillItem)
        }
        this.skillMap.push(skillItem)
      }
    },
    switchToSection () {
      this.setChapterTitle(this.dropdownSelected.chapterName)
      this.setSectionContent(this.dropdownSelected.sectionData)

      let skillItem = {}
      skillItem['name'] = this.dropdownSelected.chapterName
      skillItem['value'] = this.dropdownSelected
      this.setSkillSelected(skillItem)
    },
    onResize () {
      this.isMobile = window.innerWidth < 600
    },
    addGTMEvent () {
      let cfVideos = document.querySelectorAll('.cpd-content-container iframe')

      cfVideos.forEach(cfVideo => {
        let cfPlayer = Stream(cfVideo)
        let isOnLoop = false
        if (cfVideo.src.includes('loop=true')) {
          isOnLoop = true
        }

        cfPlayer.addEventListener('play', () => {
          this.$gtm.trackEvent({
            event: 'user_played_video',
            category: 'CPD',
            action: 'play',
            label: 'User played video',
            productName: this.bookTitle,
            chapterName: this.chapter,
            sectionTitle: this.section,
            videoTitle: this.nowPlaying,
            isMediaPlayed: 'true',
            isVideoOnLoop: isOnLoop
          })
        })

        if (!isOnLoop) {
          cfPlayer.addEventListener('ended', () => {
            this.$gtm.trackEvent({
              event: 'user_finished_video',
              category: 'CPD',
              action: 'completed',
              label: 'User finished watching video',
              productName: this.bookTitle,
              chapterName: this.chapter,
              sectionTitle: this.section,
              videoTitle: this.nowPlaying,
              isMediaFinished: 'true'
            })
          })
        }
      })
    }
  },
  computed: {
    ...mapState({
      bookTitle: state => state.book.bookTitle,
      chapterMap: state => state.book.chapterMap,
      chapterHeading: state => state.book.chapterHeading,
      chapterTitle: state => state.book.chapterTitle,
      sectionContent: state => state.book.sectionContent,
      skillSelected: state => state.book.skillSelected
    }),
    dropdownSelected: {
      get () {
        return this.skillSelected
      },
      set (value) {
        this.setSkillSelected(value)
      }
    }
  }
}
</script>

<style lang="scss">
.cpd-content-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-bottom: 50px;
  margin: auto;
  @media screen and (min-width: 600px) {
    padding: 30px;
  }
  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 7px;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.5);
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }

  .v-breadcrumbs {
    .v-breadcrumbs__item {
      color: #e6007e;
    }
  }
  .cpd-header {
    margin-top: 10px;
  }
  .back {
    height: 64px;
    background-color: #ffffff;
    text-align: left;
    font-size: 17px;
    padding: 18px 18px 18px 0;
  }
  .title-header {
    text-align: left;
    padding: 14px 14px 14px 12px;
    font-size: 18px;
    border-top: 4px solid #004855;
    font-weight: 600;
  }
  .cpd-content-body {
    margin-top: 50px;
    margin-left: 20px;
    @media screen and (max-width: 600px) {
      margin: 20px !important;
    }
    .cpd-section-toc {
      max-width: 254px;
      width: 30%;
    }
    .cpd-section {
      margin: auto;
      padding: 0px;
      @media screen and (min-width: 601px) {
        max-width: 700px;
        width: 70%;
      }
      @media screen and (max-width: 600px) {
        width: 90%;
      }
      img {
        margin: auto;
        width: 100%;
        height: 100%;
      }
      td {
        max-width: 300px;
      }
      .cpd-section-header {
        font-weight: 900;
        font-size: 36px;
        display: flex;
        justify-content: space-between;
        @media screen and (max-width: 600px) {
          flex-direction: column-reverse;
        }

        .cpd-title {
          width: 60%;
          display: inline-block;
          @media screen and (max-width: 600px) {
            width: 100%;
          }
        }
        .cpd-skills-dropdown {
          width: 40%;
          display: inline-block;
          .v-select {
            font-size: 14px;
          }
          @media screen and (max-width: 600px) {
            width: 90%;
            margin: auto;
          }
        }
      }
    }
  }
}

.v-breadcrumbs {
  padding-left: 0px !important;
  li {
    .v-breadcrumbs__item {
      font-size: 10.5px;
    }
  }
}

</style>
